<template>
  <FlexboxLayout 
    :class="classes" 
    :justifyContent="typeof action === 'string' && !loading ? 'space-between' : 'center'" 
    alignItems="center"
    :borderColor="mainColor" 
    :backgroundColor="backgroundColor" 
    :title="text" 
    role="button"
    :tabindex="disabled || loading ? -1 : (tabindex !== null ? tabindex : 0)"
    :aria-label="ariaLabel"
    :aria-disabled="disabled || loading ? 'true' : 'false'"
    :aria-pressed="pressed !== null ? pressed.toString() : null"
    :aria-describedby="describedBy"
    @tap="tap" 
    @click="click" 
    @touch="touch"
    @keydown="handleKeydown"
    ref="buttonElement"
  >
    <AgLoader v-if="loading" :color="loaderColor" aria-hidden="true" />
    <template v-else>
      <FlexboxLayout justifyContent="flex-start">
        <AgIcon 
          v-if="icon" 
          :type="icon" 
          :color="iconColorReal" 
          :size="iconSize" 
          aria-hidden="true"
        />
        <Label 
          v-if="text" 
          class="text" 
          :text="text" 
          :color="textColor" 
          :fontSize="isIos ? fontSize + 3 : fontSize" 
          :textWrap="textWrap"
          @loaded="labelLoaded" 
        />
      </FlexboxLayout>
      <AgIcon 
        v-if="action" 
        class="action" 
        :type="action" 
        :color="actionColorReal" 
        :size="actionSize" 
        aria-hidden="true"
      />
    </template>
  </FlexboxLayout>
</template>

<script>

import env from '@/helpers/env'
import { colors } from '@/helpers/ui'
import AgLoader from '@/components/Loader'

export default {
  components: {
    AgLoader
  },
  props: {
    text: { type: String },
    outlined: { type: Boolean },
    loading: { type: Boolean },
    disabled: { type: Boolean },
    round: { type: Boolean },
    large: { type: Boolean },
    small: { type: Boolean },
    flat: { type: Boolean },
    dark: { type: Boolean },
    link: { type: Boolean },
    embedded: { type: Boolean },
    action: { type: String },
    set: { type: String },
    icon: { type: String },
    iconColor: { type: String },
    iconSize: {
      type: [String, Number],
      default: 26
    },
    color: { type: String },
    actionColor: { type: String },
    actionSize: {
      type: [String, Number],
      default: env.isIos ? 13 : 16
    },
    ariaLabel: { type: String },
    describedBy: { type: String },
    pressed: { 
      type: Boolean,
      default: null
    },
    tabindex: {
      type: [Number, String],
      default: null
    },
    textWrap: {
      type: Boolean,
      default: false
    },
  },
  data: () => ({
    active: false,
    isIos: env.isIos,
    textOverflows: false
  }),
  computed: {
    classes () {
      return {
        'button': true,
        'active': this.active,
        'outlined': this.outlined,
        'disabled': this.disabled,
        'loading': this.loading,
        'round': this.round,
        'large': this.large,
        'small': this.small,
        'flat': this.flat,
        'link': this.link,
        'dark': this.dark,
        'embedded': this.embedded,
        'icon-only': this.icon && !this.text
      }
    },
    mainColor () {
      if (this.color) return this.color

      if (this.flat) {
        return this.dark ? colors.primary : colors.darktrans
      } else {
        return this.dark ? 'white' : colors.primary
      }
    },
    textColor () {
      if (this.outlined) return this.mainColor
      
      if (this.dark) {
        return this.active ? 'white' : colors.primary
      }
      
      return 'white'
    },
    iconColorReal () {     
      return this.iconColor || this.textColor
    },
    actionColorReal () {     
      return this.actionColor || this.textColor
    },
    backgroundColor () {
      if (this.outlined || this.loading) return ''
      if (this.flat) {
        return this.active ? this.mainColor : 'transparent'
      } 
      
      return this.mainColor
    },
    loaderColor () {
      if (this.color) return this.color

      if (this.flat) {
        return this.dark ? colors.primary : 'white'
      } else {
        return this.dark ? 'white' : colors.primary
      }
    },
    fontSize () {
      if (this.small) return 16
      return this.textOverflows ? 16 : 20
    }
  },
  methods: {
    tap () {
      if (!this.loading && !this.disabled) {
        this.$emit('tap')
      }
    },
    click () {
      if (!this.loading && !this.disabled) {
        this.$emit('click')
      }
    },
    touch (event) {
      if (this.loading) {
        return
      }

      if (event.action === 'down') {
        this.active = true
      } else if (event.action === 'up' || event.action === 'cancel') {
        this.active = false
      }
    },
    labelLoaded (args) {
      if (env.isAndroid) {
        args.object.android.setGravity(17)
      }
    },
    handleKeydown(event) {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        if (!this.loading && !this.disabled) {
          // Use the same pattern as working radio buttons - single click event
          this.$emit('click');
        }
      }
    },
    checkTextOverflow () {
      if (!env.isWeb || !this.$refs.buttonElement) return
      const el = this.$refs.buttonElement.$el || this.$refs.buttonElement
      if (!el || !this.text) return
      // Measure if text at 20px would overflow the button's available width
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      ctx.font = '600 20px Inter, Open Sans, sans-serif'
      const textWidth = ctx.measureText(this.text).width
      const buttonPadding = 24 // 12px padding on each side
      const availableWidth = el.clientWidth - buttonPadding
      this.textOverflows = textWidth > availableWidth
    }
  },
  mounted() {
    // For web environments, add native keyboard event listener
    if (typeof window !== 'undefined' && this.$refs.buttonElement) {
      const element = this.$refs.buttonElement.$el || this.$refs.buttonElement;
      if (element) {
        element.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (!this.loading && !this.disabled) {
              this.$emit('click');
            }
          }
        });
      }
    }
    if (env.isWeb) {
      this.$nextTick(() => this.checkTextOverflow())
      this._resizeHandler = () => this.checkTextOverflow()
      window.addEventListener('resize', this._resizeHandler)
    }
  },
  beforeDestroy () {
    if (this._resizeHandler) {
      window.removeEventListener('resize', this._resizeHandler)
    }
  }
}

</script>

<style scoped lang="scss">

.button {
  border-style: solid;
  border-color: #004E9E;
  background-color: #004E9E;
  text-transform: none;
  font-weight: normal;
  border-radius: 20px;
  border-width: 1px;
  box-shadow: 2px 2px 4px -1px rgba(0, 0, 0, 0.25);


  &:hover:not(.disabled):not(.loading):not(.outlined):not(.flat):not(.dark) {
    background-color: #0066CB !important;
    border-color: #0066CB !important;
  }

  &:active,
  &.active {
    opacity: 0.7;
  }


  &.flat {
    border: none;
    box-shadow: none;
  }

  &.flat:focus {
    outline: 2px solid #004e9e;
    outline-offset: 2px;
  }

  &.dark:focus {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
  }

  &.flat:active,
  &.flat.active {
    opacity: 1;
  }

  &.embedded {
    border-radius: 0;
  }

  &.outlined {
    background-color: rgba(255, 255, 255, 0.00);
    border-color: #004E9E;
    box-shadow: 2px 2px 4px -1px rgba(0, 0, 0, 0.25);
  }

  &.outlined:hover {
    background-color: #004E9E14;
  }

  &.loading,
  &.loading:active {
    background-color: #eee;
    border-width: 0;
  }

  &.flat.loading,
  &.flat.loading:active {
    background-color: transparent;
  }

  &.disabled {
    background-color: #E7E7E7 !important;
    border-color: #E7E7E7 !important;
    color: #6D6D6D !important;
    opacity: 1;
  }

  .text {
    font-family: "Inter", "Open Sans", sans-serif;
    font-weight: 600;
    height: 100%;
    color: #FFFFFF;
  }

  &.outlined .text {
    color: #004E9E;
  }

  &.link .text {
    font-weight: normal;
  }

  &.action {
    opacity: 0.5;
  }
}

.ns-ios,
.ns-android {
  .button {
    height: 60;
    border-radius: 20;
    padding: 2 12;

    &.embedded {
      padding-left: 20;
      padding-right: 20;
      border-radius: 0;
    }

    &.outlined {
      border-width: 2;
      padding: 0 10;
    }

    &.icon-only {
      width: 60;
    }

    &.round {
      border-radius: 30;
    }

    &.big .text {
      font-size: 20;
    }

    &.small {
      height: 40;
    }

    .text.wrap {
      line-height: normal;
      align-self: center;
      text-align: center;
    }

    .icon {
      width: 36;
      margin-right: 5;
      align-self: center;
    }

    &.icon-only .icon {
      margin-right: 0;
    }

    .icon.action {
      width: 20;
      margin-left: 5;
      margin-right: 0;
      align-self: center;
    }
  }
}

html {
  .button {
    height: 60px;
    font-size: 16px;
    border-radius: 20px;
    padding: 2px 12px;
    box-shadow: 2px 2px 4px -1px rgba(0, 0, 0, 0.25);
    cursor: pointer;
    user-select: none;
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;

    &.flat {
      border: none;
      box-shadow: none;
    }

    &.embedded {
      padding-left: 20px;
      padding-right: 20px;
    }

    &.outlined {
      border-width: 1px;
      padding: 0 10px;
      background: rgba(255, 255, 255, 0.00);
      box-shadow: 2px 2px 4px -1px rgba(0, 0, 0, 0.25);
    }

    &.icon-only {
      width: 60px;
    }

    &.round {
      border-radius: 30px;
    }

    &.disabled,
    &.loading {
      cursor: default;
    }

    &.small {
      height: 40px;
    }

    .text {
      position: relative;
      align-items: center;
      line-height: 56px;
    }

    &.small .text {
      line-height: 40px;
    }

    .text.wrap {
      line-height: normal;
      align-self: center;
      text-align: center;
    }

    &.big .text {
      font-size: 18px;
      font-family: 'Inter', 'Roboto Condensed';
    }

    .icon {
      width: 36px;
      margin-right: 5px;
      align-self: center;
    }

    &.icon-only .icon {
      margin-right: 0;
    }

    .icon.action {
      width: 20px;
      margin-left: 5px;
      margin-right: 0;
      align-self: center;
    }
  }
}

</style>